import { useEffect, useRef } from 'react';

// Polls the currently-loaded rows that are still "in flight" (e.g. a file upload
// whose classification hasn't finished) and patches each one in place as its
// status resolves — WITHOUT touching the cursor, sort, or scroll. It fetches each
// in-flight row by id and hands the fresh record to updateItem (which replaces
// that one row by key), so a row that flips received -> classified updates where
// it sits instead of the whole list resetting and reordering.
//
// Only loaded rows are watched: a row that isn't in the current window is ignored
// (nothing to update in place). Polling runs only while at least one row is in
// flight and stops as soon as they all resolve.

interface Options<T> {
  /** The currently-loaded rows (usePaginatedQuery's allItems). */
  items: T[];
  /** True while a row is still being processed and worth polling. */
  isInFlight: (item: T) => boolean;
  /** Row identity — pass the same getRowKey the table/hook use. */
  getKey: (item: T) => string;
  /** Fetch one fresh row by id. */
  fetchOne: (id: string) => Promise<T>;
  /** Patch one loaded row in place (usePaginatedQuery's updateItem). */
  updateItem: (id: string, updater: (prev: T) => T) => void;
  /** Poll cadence in ms. Default 3000. */
  intervalMs?: number;
  /** Gate the poll (e.g. only while this tab's path is active). Default true. */
  enabled?: boolean;
}

export function usePollInFlight<T>({
  items,
  isInFlight,
  getKey,
  fetchOne,
  updateItem,
  intervalMs = 3000,
  enabled = true,
}: Options<T>): void {
  const inFlightIds = items.filter(isInFlight).map(getKey);
  // Restart the interval only when the SET of in-flight ids changes, not on
  // every render (items is a fresh array each time).
  const idsKey = inFlightIds.slice().sort().join(',');

  const idsRef = useRef<string[]>(inFlightIds);
  idsRef.current = inFlightIds;
  const fetchOneRef = useRef(fetchOne);
  fetchOneRef.current = fetchOne;
  const updateItemRef = useRef(updateItem);
  updateItemRef.current = updateItem;

  useEffect(() => {
    if (!enabled || idsKey === '') return;
    let cancelled = false;

    const tick = async () => {
      for (const id of idsRef.current) {
        try {
          const fresh = await fetchOneRef.current(id);
          if (cancelled) return;
          updateItemRef.current(id, () => fresh);
        } catch {
          // 404 (row gone) or a transient error — leave the row as-is; it will
          // retry next tick, or drop out of the watch set once it resolves.
        }
      }
    };

    const h = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(h);
    };
  }, [idsKey, enabled, intervalMs]);
}
